import { useCallback, useContext, useEffect, useState } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { UploadFileRepository } from 'interfaceAdapter/repositories/UploadFile';

export interface ImageUploadProps {
    imagePath: string;
    directory:
        | 'company_profile'
        | 'agent_profile'
        | 'company'
        | 'agent'
        | 'buyer_profile'
        | 'buyer'
        | 'supporter_profile'
        | 'supporter';
    fileName: string;
    fileCategory: 'agent' | 'company' | 'buyer' | 'supporter';
    disabled?: boolean;
    width?: number;
    height?: number;
    urlId?: string | null;
    isValid?: boolean;
    onComplete: (url: string) => void;
}

export const useImageUpload = ({
    imagePath,
    directory,
    fileName,
    fileCategory,
    width = 180,
    height = 180,
    urlId,
    onComplete,
}: ImageUploadProps) => {
    const diContainer = useContext(diContainerContext);
    const uploadFileRepository = diContainer.resolve(UploadFileRepository);
    const reader = new FileReader();
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isValid, setIsValid] = useState<boolean | undefined>(undefined);

    const onInput = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            reader.readAsDataURL(file);
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
                setIsValid(true); // 簡易的に画像が選択された時点で有効とする
            };

            setUploading(true);
            try {
                const upload = uploadFileRepository.upload(
                    file,
                    fileName,
                    directory,
                    fileCategory,
                    width,
                    height,
                    urlId,
                );
                const fileUrl = await upload;
                onComplete(fileUrl);
            } catch (error) {
                setIsValid(false); // アップロードエラーが発生した場合は無効とする
            } finally {
                setUploading(false);
            }

            // 空にしないと同じファイルを選択したときにonInputが発火しない
            e.target.value = '';
        },
        [directory, fileName, fileCategory, width, height, urlId, onComplete],
    );

    useEffect(() => {
        if (!imagePath) return;
        setPreviewUrl(`${imagePath}?hash=${Math.floor(Math.random() * 10000)}`);
        setIsValid(true); // すでに画像がある場合は有効とする
    }, [imagePath]);

    return {
        previewUrl,
        uploading,
        onInput,
        isValid,
    };
};
